import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { CToast, CToastBody, CToastHeader, CToaster } from "@coreui/react";

const ToastContext = createContext();
export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [prefs, setPrefs] = useState({
        allowNewNotifications: false,
        allowNewMessages: false,
        allowToasts: false,
        allowNewNotificationsSong: false,
        allowNewMessagesSong: false
    });

    const prevNotificationsRef = useRef([]);
    const prevMessagesRef = useRef([]);
    const notifySound = useRef(new Audio(process.env.PUBLIC_URL + "/assets/sounds/notify.wav"));

    // Charger les préférences depuis localStorage
    const loadPrefs = () => {
        const allowNewNotifications = localStorage.getItem("allowNewNotifications") === "true";
        const allowNewMessages = localStorage.getItem("allowNewMessages") === "true";
        const allowToasts = localStorage.getItem("allowToasts") === "true";

        // Valeurs par défaut pour les sons
        let allowNewNotificationsSong = localStorage.getItem("allowNewNotificationsSong") === "false";
        let allowNewMessagesSong = localStorage.getItem("allowNewMessagesSong") === "false";

        // Variables dérivées en fonction des toasts et notifications/messages
        if (allowNewNotifications && allowToasts) {
            allowNewNotificationsSong = localStorage.getItem("allowNewNotificationsSong") === "true";
        }
        if (allowNewMessages && allowToasts) {
            allowNewMessagesSong = localStorage.getItem("allowNewMessagesSong") === "true";
        }

        setPrefs({
            allowNewNotifications,
            allowNewMessages,
            allowToasts,
            allowNewNotificationsSong,
            allowNewMessagesSong
        });
    };

    useEffect(() => {
        loadPrefs();

        // Écoute des changements depuis d'autres onglets
        const handleStorageChange = (e) => {
            if ([
                "allowNewNotifications",
                "allowNewMessages",
                "allowToasts",
                "allowNewNotificationsSong",
                "allowNewMessagesSong"
            ].includes(e.key)) {
                loadPrefs();
            }
        };
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    const pushToast = (title, message, color = "primary") => {
        if (!prefs.allowToasts) return;

        const id = Date.now();
        setToasts(prev => [...prev, { id, title, message, color }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 7000);
    };

    const playSound = () => {
        notifySound.current.play().catch(err => {
            console.error('Impossible de lire le son de notification:', err);
        });
    };

    const handleNewAlerts = (notifications = [], messages = []) => {
        // Notifications
        if (prefs.allowNewNotifications) {
            const newNotifs = notifications.filter(
                n => !prevNotificationsRef.current.some(prev => prev.id === n.id)
            );
            newNotifs.forEach(n => pushToast("Nouvelle notification", n.title || n.message, "info"));
            if (prefs.allowNewNotificationsSong && newNotifs.length > 0) playSound();
        }

        // Messages
        if (prefs.allowNewMessages) {
            const newMsgs = messages.filter(
                m => !prevMessagesRef.current.some(prev => prev.id === m.id)
            );
            newMsgs.forEach(m => pushToast("Nouveau message", m.subject || m.content, "success"));
            if (prefs.allowNewMessagesSong && newMsgs.length > 0) playSound();
        }

        prevNotificationsRef.current = notifications;
        prevMessagesRef.current = messages;
    };

    return (
        <ToastContext.Provider value={{ pushToast, handleNewAlerts, reloadPrefs: loadPrefs }}>
            {children}

            <CToaster placement="top-end">
                {toasts.map(t => (
                    <CToast key={t.id} autohide visible color={t.color}>
                        <CToastHeader closeButton>
                            <strong>{t.title}</strong>
                        </CToastHeader>
                        <CToastBody>{t.message}</CToastBody>
                    </CToast>
                ))}
            </CToaster>
        </ToastContext.Provider>
    );
};
